import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import "./Sidebar.css";

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen, activeTab, setActiveTab, setIsLogoutConfirmOpen, onCommandXClick, userEmail, userName }) => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [theme, setTheme] = useState(document.documentElement.getAttribute('data-theme') || 'dark');
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) setIsSidebarOpen(false);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [setIsSidebarOpen]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setTheme(document.documentElement.getAttribute('data-theme') || 'dark');
    });
    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isUserMenuOpen]);

  const isLight = theme === 'light';
  const hamburgerStyle = {
    position: "fixed",
    left: isSidebarOpen ? "213px" : "16px",
    top: "16px",
    cursor: "pointer",
    background: isLight ? "transparent" : "#000000",
    padding: "7px",
    borderRadius: "8px",
    border: isLight ? "none" : "1px solid rgb(27, 172, 205)",
    zIndex: 3000,
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
    boxShadow: !isSidebarOpen ? (isLight ? "none" : "0 0 10px rgba(0, 224, 255, 0.5)") : "none"
  };

  const lineStyle = {
    width: "20px",
    height: "3px",
    background: isLight ? "#54bbcd" : "#c1b2b2",
    borderRadius: "2px"
  };

  return (
    <>
      {/* 1. HAMBURGER BUTTON */}
      <div
        onClick={() => setIsSidebarOpen(!isSidebarOpen)}
        style={hamburgerStyle}
      >
        <div style={lineStyle}></div>
        <div style={lineStyle}></div>
        <div style={lineStyle}></div>
      </div>

      {/* 2. SIDEBAR ASIDE */}
      <aside className="sidebar" style={{
        // Width aur minWidth dono ko zero kar diya jab sidebar band ho
        width: isSidebarOpen ? "260px" : "0px",
        minWidth: isSidebarOpen ? "260px" : "0px",
        position: isMobile ? "fixed" : "relative",
        left: isMobile && !isSidebarOpen ? "-300px" : "0", 
        height: "100vh",
        transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        overflow: "hidden", // 👈 Extra content ya borders chupane k liye
        background: "#000000",
        // Border tabhi dikhega jab sidebar khula ho
        borderRight: isSidebarOpen ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
        zIndex: 2000 
      }}>
        
        <div style={{
          padding: isMobile ? "100px 15px 20px" : "80px 15px 20px", 
          width: "240px", // Internal width fixed rakhi ha
          opacity: isSidebarOpen ? 1 : 0,
          transition: "opacity 0.2s ease",
          boxSizing: "border-box",
          visibility: isSidebarOpen ? "visible" : "hidden",
          display: "flex",
          flexDirection: "column",
          height: "100%"
        }}>
          
          {/* LOGO SECTION */}
          <div style={{ marginBottom: '30px', textAlign: 'center' }}>
            <h1 style={{ fontSize: isMobile ? '40px' : '50px', marginBottom: '0', lineHeight: '1', color: '#fff' }}>Cortex</h1>
          </div>

          {/* NAVIGATION LINKS */}
          <ul style={{ listStyle: 'none', padding: 0, marginTop: '20px', width: '100%', flex: 1 }}>
            {[
              { id: 'dashboard', icon: '💎', label: 'Dashboard', path: '/dashboard' },
              { id: 'tasks', icon: '📂', label: 'Tasks', path: '/tasks' },
              { id: 'calendar', icon: '🗓️', label: 'Calendar', path: '/calendar' },
              { id: 'agent', icon: '⚡', label: 'CommandX', path: '/agent' },
              { id: 'settings', icon: '⚙️', label: 'Settings', path: '/settings' }
            ].map((item) => (
              <li
                key={item.id}
                onClick={() => {
                  // mark that user clicked CommandX so Agent can show animation after navigation
                  try { if (item.id === 'agent') sessionStorage.setItem('commandxTrigger', 'true'); } catch (e) {}
                  setActiveTab(item.id);
                  // navigate if a path is provided
                  if (item.path) navigate(item.path);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 15px',
                  cursor: 'pointer',
                  borderRadius: '10px',
                  marginBottom: '10px',
                  transition: 'all 0.3s ease',
                  background: activeTab === item.id ? "rgba(0, 224, 255, 0.1)" : "transparent",
                  color: activeTab === item.id ? "#00e0ff" : "#fff",
                  border: activeTab === item.id ? "1px solid rgba(0, 224, 255, 0.2)" : "1px solid transparent"
                }}
              >
                <span style={{ marginRight: "12px", fontSize: '18px' }}>{item.icon}</span>
                <span style={{ fontSize: '14px', fontWeight: '600' }}>{item.label}</span>
              </li>
            ))}
          </ul>

          <div className="sidebar-footer" ref={userMenuRef}>
            <div 
              className={`user-pill ${isUserMenuOpen ? 'open' : ''}`}
              onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            >
              <div className="user-avatar">
                {((userName && userName[0]) || (userEmail && userEmail[0]) || 'U').toUpperCase()}
              </div>
              <div className="user-meta">
                <p className="user-name">{userName || 'User'}</p>
              </div>
              <span className="caret">▾</span>
            </div>

            {isUserMenuOpen && (
              <div className="user-menu">
                <button onClick={() => setIsLogoutConfirmOpen(true)}>
                  <span style={{ fontSize: '16px' }}>↪]</span>
                  <span>Log out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* 3. MOBILE OVERLAY */}
      {isMobile && isSidebarOpen && (
        <div 
          onClick={() => setIsSidebarOpen(false)}
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            zIndex: 1500,
            backdropFilter: 'blur(3px)'
          }}
        />
      )}
    </>
  );
};

export default Sidebar;